import { assoc, filter, length, map, propOr, reduce } from 'ramda';
import { v4 } from 'uuid';

import ooeConstants from '../constants';
import { selectIndexedMenuItems } from '../menu/selectors';
import { MenuItem } from '../menu/types';
import { OrderLineItem, OrderLineItemComboItem } from '../order/schemas';
import { IndexedNutrition } from '../types/nutrition';
import { indexByTag } from '../util/utils';
import { selectCartWithPrices } from './selectors';

const mapModifierToCartModifier = ({
  itemTag,
  quantity = 1,
  comboTag,
  maximum,
}: {
  itemTag: string;
  quantity?: number;
  comboTag?: string;
  maximum?: number;
}) => ({
  quantity,
  comboTag,
  tag: itemTag,
  maximum,
});

function findSide(lineItem: OrderLineItem) {
  const { comboItems } = lineItem;
  if (comboItems) {
    return comboItems[1];
  }
  return {};
}

function findPremiumSide(lineItem: OrderLineItem) {
  const { comboItems } = lineItem;
  if (comboItems && comboItems.length === 4) {
    return comboItems[2];
  }
  return {};
}

function findDessert(lineItem: OrderLineItem) {
  const { comboItems } = lineItem;
  if (comboItems) {
    return comboItems[comboItems.length === 4 ? 3 : 2];
  }
  return {};
}

function getComboModifiers(lineItem: OrderLineItem) {
  const { comboItems } = lineItem;
  if (comboItems) {
    const comboModifiers = reduce(
      (acc, value) => {
        const { modifiers, itemTag } = value;
        if (Array.isArray(modifiers) && length(modifiers) > 0) {
          const taggedModifiers = map(assoc('comboTag', itemTag), modifiers);
          return [...acc, ...taggedModifiers];
        }
        return acc;
      },
      [] as OrderLineItemComboItem[],
      comboItems,
    );

    return comboModifiers;
  }
  return [];
}

function getAllModifiers(lineItem: OrderLineItem) {
  const itemModifiers = propOr<
    Array<{
      itemTag: string;
      quantity: number;
    }>,
    typeof lineItem,
    Array<{
      itemTag: string;
      quantity: number;
    }>
  >([], 'modifiers', lineItem);
  const comboModifiers = getComboModifiers(lineItem);
  const allModifiers = [...itemModifiers, ...comboModifiers];
  return map(mapModifierToCartModifier, allModifiers);
}

function getIndexedModifiers(lineItem: OrderLineItem): Record<string, CartItemModifier> {
  const allModifiers = getAllModifiers(lineItem);
  return indexByTag(allModifiers);
}

export function mapLineItemToCartItem(lineItem: OrderLineItem): CartItem {
  const { itemTag, promoFree } = lineItem;
  const baseCartItem = {
    tag: itemTag,
    quantity: propOr<number, typeof lineItem, number>(1, 'quantity', lineItem),
    specialInstructions: propOr<string, typeof lineItem, string>('', 'specialInstructions', lineItem),
    promoFree,
  };

  const modifiers = getIndexedModifiers(lineItem);
  const selectedSideTag = propOr<undefined, ReturnType<typeof findSide>, undefined>(
    undefined,
    'itemTag',
    findSide(lineItem),
  );
  const selectedPremiumSideTag = propOr<undefined, ReturnType<typeof findPremiumSide>, undefined>(
    undefined,
    'itemTag',
    findPremiumSide(lineItem),
  );
  const selectedDessertTag = propOr<undefined, ReturnType<typeof findDessert>, undefined>(
    undefined,
    'itemTag',
    findDessert(lineItem),
  );

  return {
    ...baseCartItem,
    selectedSideTag,
    selectedDessertTag,
    selectedPremiumSideTag,
    modifiers,
    id: v4(),
  };
}

export const convertLineItemsToCartItems = (lineItems: OrderLineItem[]): CartItem[] => {
  return map((i) => mapLineItemToCartItem(i), lineItems);
};

export const mapModifierToView = (
  nutrition: IndexedNutrition,
  {
    tag,
    name,
    quantity,
    desktopImage,
    pdpImages,
    mobileImage,
    modifierType,
    itemPrice,
    leadTime,
    disabled,
    comboTag,
    itemGroupType,
    modifierTypeGrouping,
    maximum,
  }: ReturnType<typeof selectCartWithPrices>[number]['items'][number],
): ModifierView => ({
  tag,
  type: modifierType,
  imgUrl: desktopImage ?? pdpImages ?? mobileImage,
  name: name ?? tag,
  quantity,
  price: itemPrice,
  leadTime,
  disabled,
  comboTag,
  groupType: itemGroupType,
  grouping: modifierTypeGrouping,
  toggleable: ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(tag),
  defaultAvailable: !!modifierType && ooeConstants.DEFAULT_AVAILABLE_MODIFIER_TYPES.includes(modifierType),
  calories: nutrition[tag]?.nutrition.calories.total,
  maximum,
});

export const mapDessertToView = ({
  tag,
  name,
  desktopImage,
  pdpImages,
  mobileImage,
  modifierType,
  itemPrice,
  leadTime,
  disabled,
  comboTag,
  itemGroupType,
  modifierTypeGrouping,
}: NonNullable<ReturnType<typeof selectCartWithPrices>[number]['selectedDessert']>): ModifierView => ({
  tag,
  type: modifierType,
  imgUrl: desktopImage ?? pdpImages ?? mobileImage,
  name: name ?? tag,
  quantity: 1,
  price: itemPrice,
  leadTime,
  disabled,
  comboTag,
  groupType: itemGroupType,
  grouping: modifierTypeGrouping,
  toggleable: ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(tag),
  defaultAvailable: !!modifierType && ooeConstants.DEFAULT_AVAILABLE_MODIFIER_TYPES.includes(modifierType),
});

export const mapSideToView = ({
  tag,
  name,
  desktopImage,
  pdpImages,
  mobileImage,
  modifierType,
  itemPrice,
  leadTime,
  disabled,
  comboTag,
  itemGroupType,
  modifierTypeGrouping,
}: NonNullable<ReturnType<typeof selectCartWithPrices>[number]['selectedSide']>): ModifierView => ({
  tag,
  type: modifierType,
  imgUrl: desktopImage ?? pdpImages ?? mobileImage,
  name: name ?? tag,
  quantity: 1,
  price: itemPrice,
  leadTime,
  disabled,
  comboTag,
  groupType: itemGroupType,
  grouping: modifierTypeGrouping,
  toggleable: ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(tag),
  defaultAvailable: !!modifierType && ooeConstants.DEFAULT_AVAILABLE_MODIFIER_TYPES.includes(modifierType),
});

export const mapPremiumSideToView = ({
  tag,
  name,
  desktopImage,
  pdpImages,
  mobileImage,
  modifierType,
  itemPrice,
  leadTime,
  disabled,
  comboTag,
  itemGroupType,
  modifierTypeGrouping,
}: NonNullable<ReturnType<typeof selectCartWithPrices>[number]['selectedPremiumSide']>): ModifierView => ({
  tag: tag ?? '',
  type: modifierType,
  imgUrl: desktopImage ?? pdpImages ?? mobileImage,
  name: name ?? tag ?? '',
  quantity: 1,
  price: itemPrice ?? 0,
  leadTime,
  disabled,
  comboTag,
  groupType: itemGroupType,
  grouping: modifierTypeGrouping,
  toggleable: ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(tag),
  defaultAvailable: !!modifierType && ooeConstants.DEFAULT_AVAILABLE_MODIFIER_TYPES.includes(modifierType),
});

export const mapCartItemToView = (
  nutrition: IndexedNutrition,
  item: ReturnType<typeof selectCartWithPrices>[number],
): CartItemView => ({
  ...item,
  dessertItem: item.selectedDessert ? mapDessertToView(item.selectedDessert) : undefined,
  sideItem: item.selectedSide ? mapSideToView(item.selectedSide) : undefined,
  premiumSideItem: item.selectedPremiumSide ? mapPremiumSideToView(item.selectedPremiumSide) : undefined,

  otherItems: item.items.map((modifier) => mapModifierToView(nutrition, modifier)),
});

export const mapWithQuantityForToggleableModifiers = (modifier: ModifierView) => {
  const toggleableChecked = modifier.defaultAvailable ? !modifier.quantity : !!modifier.quantity;
  return { ...modifier, quantity: modifier.toggleable ? (toggleableChecked ? 1 : 0) : modifier.quantity };
};

/**
 * This exists to account for a structural inconsistency in the the DXE menu v3.1.
 *
 * Sandwich items have two modifier groups, REMOVE_MOD and PICKLES_OLO_MOD,
 *  that both contain a modifier with a tag of PICKLES.
 *
 * One ends up overwriting the other when we index the menu items by tag.
 *
 * The pickles modifier that overwrites happens to be an EXTRA modifier type.
 *
 * We need to have a modifier type of RECIPE so that pickles
 *  are included by default for sandwiches.
 */
export const getModTypeThatAccountsForPickles = ({
  itemTag,
  modifierTag,
  modifierType,
}: {
  itemTag: string;
  modifierTag: string;
  modifierType?: string;
}) =>
  (itemTag === 'SANDWICH_CFA_CHICKEN' || itemTag === 'SPICY_CHICKEN_CFA_SANDWICH') &&
  modifierTag === 'PICKLES'
    ? 'RECIPE'
    : modifierType;

export function mapModifiersToLineItems(
  modifiers: ReturnType<typeof selectCartWithPrices>[number]['modifiers'],
) {
  return modifiers.map((mod) => {
    return {
      action:
        mod.modifierType && ooeConstants.DEFAULT_AVAILABLE_MODIFIER_TYPES.includes(mod.modifierType)
          ? 'REMOVE'
          : 'ADD',
      itemTag: mod.tag,
      name: mod.name,
      quantity: mod.quantity,
      comboTag: mod.comboTag,
    };
  });
}

export function mapCartItems(
  items: ReturnType<typeof selectCartWithPrices> | MenuItem[],
  menuItems: ReturnType<typeof selectIndexedMenuItems>,
  comboModifiers?: ReturnType<typeof selectCartWithPrices>[number]['modifiers'],
  selectedSide?: ReturnType<typeof selectCartWithPrices>[number]['selectedSide'],
  selectedDessert?: ReturnType<typeof selectCartWithPrices>[number]['selectedDessert'],
  selectedPremiumSide?: ReturnType<typeof selectCartWithPrices>[number]['selectedPremiumSide'],

  comboItemQty = 1, //hardcode combo item quantity because promo free doesn't work without it
): Array<{
  itemTag: string;
  quantity: number;
  name?: string;
  specialInstructions?: string;
  modifiers: ReturnType<typeof mapModifiersToLineItems>;
  comboItems?: ReturnType<typeof mapCartItems>;
  dayPart?: string;
  promoFree?: boolean;
}> {
  return items.map((item) => {
    let comboItems: ReturnType<typeof mapCartItems>[number]['comboItems'] | undefined;
    let modifiers = (item as ReturnType<typeof selectCartWithPrices>[number]).modifiers;
    const menuItem: ReturnType<typeof selectIndexedMenuItems>[string] | undefined = menuItems[item.tag];

    if (menuItem?.comboItems) {
      comboItems = mapCartItems(
        menuItem.comboItems || [],
        menuItems,
        (item as ReturnType<typeof selectCartWithPrices>[number]).modifiers,
        (item as ReturnType<typeof selectCartWithPrices>[number]).selectedSide,
        (item as ReturnType<typeof selectCartWithPrices>[number]).selectedDessert,
        (item as ReturnType<typeof selectCartWithPrices>[number]).selectedPremiumSide,
      );
      modifiers = [];
    }

    let finalItem: (
      | typeof item
      | typeof selectedSide
      | typeof selectedDessert
      | typeof selectedPremiumSide
    ) & {
      tag: string;
      quantity?: number;
      name?: string;
      specialInstructions?: string;
      dayPart?: string;
      promoFree?: boolean;
    } = { ...item, name: item.name ?? menuItem?.name };
    if (selectedSide && (item as MenuItem)?.itemType === 'SIDES_GROUP') {
      finalItem = selectedSide;
    }

    if (selectedPremiumSide && (item as MenuItem)?.tag === 'PREMIUM_SIDES') {
      finalItem = selectedPremiumSide;
    }

    if (selectedDessert && (item as MenuItem)?.itemType === 'DESSERTS_GROUP') {
      finalItem = selectedDessert;
    }

    if (comboModifiers) {
      modifiers = filter((val) => val.comboTag === finalItem.tag, comboModifiers);
    }

    return {
      itemTag: finalItem.tag,
      quantity: finalItem.quantity || comboItemQty,
      name: finalItem.name,
      specialInstructions: finalItem.specialInstructions,
      modifiers: mapModifiersToLineItems(modifiers),
      comboItems,
      dayPart: finalItem.dayPart,
      promoFree: finalItem.promoFree,
    };
  });
}
